import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Building2,
  Users,
  Briefcase,
  Award,
  DollarSign,
  Shield,
  FileText,
  Upload,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import DocumentUploadModal from '../components/DocumentUploadModal';
import {
  KnowledgeBaseApi,
  CompanyProfile,
  CompanyProfileUpdateRequest,
  CategoryInfo,
  CATEGORY_GROUPS,
  CATEGORY_ICONS,
  CompanyDocumentCategory,
  CompanyDocumentCreateRequest,
} from '../services/knowledgeBaseApi';

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0;
`;

const WizardContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const WizardHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const WizardTitle = styled.h1`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[2]};
`;

const WizardDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.lg};
`;

// Progress Stepper
const StepperContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spacing[8]};
`;

const StepperLine = styled.div`
  display: flex;
  align-items: center;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeight.semibold};
  transition: all ${theme.transitions.base};

  ${props =>
    props.completed
      ? `
    background: ${theme.colors.success[500]};
    color: white;
  `
      : props.active
      ? `
    background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
    color: white;
  `
      : `
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textMuted};
    border: 2px solid ${theme.colors.border};
  `}
`;

const StepLabel = styled.div<{ active: boolean }>`
  margin-top: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${props =>
    props.active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  color: ${props => (props.active ? theme.colors.textPrimary : theme.colors.textMuted)};
`;

const StepConnector = styled.div<{ completed: boolean }>`
  width: 80px;
  height: 2px;
  background: ${props =>
    props.completed ? theme.colors.success[500] : theme.colors.border};
  margin: 0 ${theme.spacing[2]};
`;

// Form Sections
const FormSection = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

const FormSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const FormSectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[600]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormSectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ fullWidth?: boolean }>`
  grid-column: ${props => (props.fullWidth ? 'span 2' : 'span 1')};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-column: span 1;
  }
`;

const Label = styled.label<{ $hasError?: boolean }>`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${props => props.$hasError ? theme.colors.error[600] : theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[2]};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${props => props.$hasError ? theme.colors.error[500] : theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.background};
  color: ${theme.colors.textPrimary};
  transition: border-color ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error[500] : theme.colors.primary[500]};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.background};
  color: ${theme.colors.textPrimary};
  min-height: 100px;
  resize: vertical;
  transition: border-color ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.background};
  color: ${theme.colors.textPrimary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

// Document Upload Section
const CategoryGroupContainer = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const CategoryGroupHeader = styled.div<{ isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.backgroundTertiary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
  }
`;

const CategoryGroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const GroupEmoji = styled.span`
  font-size: ${theme.typography.fontSize.xl};
`;

const GroupName = styled.span`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
`;

const GroupBadge = styled.span<{ hasDocuments: boolean }>`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  background: ${props =>
    props.hasDocuments ? theme.colors.success[100] : theme.colors.backgroundTertiary};
  color: ${props =>
    props.hasDocuments ? theme.colors.success[700] : theme.colors.textMuted};
`;

const CategoryList = styled.div`
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const CategoryItem = styled.div<{ hasDocument: boolean; isRecommended: boolean; isCritical?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[4]};
  border: 1px solid ${props =>
    props.hasDocument
      ? theme.colors.success[500]
      : props.isCritical
      ? theme.colors.error[500]
      : props.isRecommended
      ? theme.colors.warning[500]
      : theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background};
`;

const CategoryDetails = styled.div`
  flex: 1;
`;

const CategoryName = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const RecommendedBadge = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  background: ${theme.colors.warning[100]};
  color: ${theme.colors.warning[700]};
`;

const CriticalBadge = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  background: ${theme.colors.error[100]};
  color: ${theme.colors.error[700]};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const CategoryDesc = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
`;

// Non-interactive "X uploaded" pill — shown beside the Upload button when
// the category already has docs. Previously the upload button itself
// flipped into a green status pill, which lost the affordance to add
// more docs (users assumed the category was "done"). Splitting into
// status + action keeps the count visible and the upload action obvious.
const UploadCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.success[100]};
  color: ${theme.colors.success[700]};
  border: 1px solid ${theme.colors.success[500]};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  white-space: nowrap;
`;

const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
`;

// Navigation
const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing[8]};
`;

const NavButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  ${props =>
    props.variant === 'primary'
      ? `
    background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
    color: white;
    border: none;
    box-shadow: ${theme.shadows.md};

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.lg};
    }
  `
      : `
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textPrimary};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.backgroundSecondary};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Tips
const TipBox = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[6]};
`;

const TipIcon = styled.div`
  color: ${theme.colors.primary[500]};
  flex-shrink: 0;
`;

const TipContent = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.primary[700]};
`;

const FieldError = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.error[600]};
  margin-top: ${theme.spacing[1]};
`;

// =============================================================================
// TYPES
// =============================================================================

interface WizardStep {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface ValidationErrors {
  company_name?: string;
}

const WIZARD_STEPS: WizardStep[] = [
  { id: 'company', label: 'Company Info', icon: <Building2 size={20} /> },
  { id: 'performance', label: 'Performance & Capabilities', icon: <Briefcase size={20} /> },
  { id: 'team', label: 'Team & Experience', icon: <Users size={20} /> },
  { id: 'certifications', label: 'Certifications', icon: <Award size={20} /> },
  { id: 'policies', label: 'Policies', icon: <Shield size={20} /> },
  { id: 'review', label: 'Review', icon: <Check size={20} /> },
];

const INDUSTRY_OPTIONS = [
  'Construction & Infrastructure',
  'Information Technology',
  'Healthcare & Pharmaceutical',
  'Financial Services',
  'Manufacturing',
  'Professional Services',
  'Education',
  'Energy & Utilities',
  'Retail & Consumer',
  'Transportation & Logistics',
  'Government & Public Sector',
  'Other',
];

const EMPLOYEE_COUNT_OPTIONS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+',
];

// =============================================================================
// COMPONENT
// =============================================================================

const KnowledgeBaseSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUploadCategory, setSelectedUploadCategory] = useState<string | undefined>(undefined);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Form state
  // Note: company_overview and mission_statement are intentionally absent.
  // Those long-form profile fields are edited on ProfilePage now; the wizard
  // owns identity + KB document slots only. Leaving them off the payload means
  // wizard saves use exclude_unset semantics and never overwrite ProfilePage edits.
  const [formData, setFormData] = useState<CompanyProfileUpdateRequest>({
    company_name: '',
    trading_name: '',
    primary_industry: '',
    employee_count: '',
    year_established: undefined,
    headquarters_country: '',
    website: '',
    // Performance & Capabilities fields
    performance_summary: '',
    on_time_delivery_rate: undefined,
    budget_adherence_rate: undefined,
    client_satisfaction_score: undefined,
    capabilities_overview: '',
    risk_management_approach: '',
    innovation_approach: '',
    current_capacity: '',
    scalability_description: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [profileRes, categoriesRes] = await Promise.all([
          KnowledgeBaseApi.getProfile(),
          KnowledgeBaseApi.getCategories(),
        ]);

        if (profileRes.success && profileRes.data) {
          setProfile(profileRes.data);
          setFormData({
            company_name: profileRes.data.company_name || '',
            trading_name: profileRes.data.trading_name || '',
            primary_industry: profileRes.data.primary_industry || '',
            employee_count: profileRes.data.employee_count || '',
            year_established: profileRes.data.year_established,
            headquarters_country: profileRes.data.headquarters_country || '',
            website: profileRes.data.website || '',
            // Performance & Capabilities fields
            performance_summary: profileRes.data.performance_summary || '',
            on_time_delivery_rate: profileRes.data.on_time_delivery_rate,
            budget_adherence_rate: profileRes.data.budget_adherence_rate,
            client_satisfaction_score: profileRes.data.client_satisfaction_score,
            capabilities_overview: profileRes.data.capabilities_overview || '',
            risk_management_approach: profileRes.data.risk_management_approach || '',
            innovation_approach: profileRes.data.innovation_approach || '',
            current_capacity: profileRes.data.current_capacity || '',
            scalability_description: profileRes.data.scalability_description || '',
          });
        }

        if (categoriesRes.success && categoriesRes.data) {
          setCategories(categoriesRes.data);
        }
      } catch (error) {
        console.error('Failed to load setup data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateCompanyInfoStep = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.company_name || formData.company_name.trim() === '') {
      errors.company_name = 'Company name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await KnowledgeBaseApi.updateProfile(formData);
    } catch (error) {
      console.error('Failed to save profile', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Validate and save Company Info step before proceeding
    if (currentStep === 0) {
      if (!validateCompanyInfoStep()) {
        return; // Don't proceed if validation fails
      }
      await handleSaveProfile();
    }

    // Save Performance step before proceeding
    if (currentStep === 1) { // Performance step is now index 1
      await handleSaveProfile();
    }

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    await handleSaveProfile();
    navigate('/knowledge-base');
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const getStepCategories = (stepId: string): string[] => {
    switch (stepId) {
      case 'company':
        return ['company_overview', 'mission_vision', 'organizational_chart', 'company_history'];
      // The Performance & Capabilities step renders text fields for the
      // profile-level summary and adds upload tiles for two of the most
      // requested KB document slots: methodology (one of the 7 critical
      // categories) and capability_statement.
      case 'performance':
        return ['methodology', 'capability_statement'];
      case 'team':
        return ['team_cv', 'key_personnel', 'team_structure', 'qualifications', 'case_study', 'project_description', 'client_reference'];
      case 'certifications':
        return ['iso_certification', 'industry_accreditation', 'quality_assurance', 'insurance_certificate', 'financial_statement'];
      case 'policies':
        return ['health_safety_policy', 'environmental_policy', 'equality_diversity_policy', 'modern_slavery_statement', 'data_protection_policy', 'business_continuity_plan'];
      default:
        return [];
    }
  };

  const getCategoriesForStep = (stepId: string) => {
    const stepCats = getStepCategories(stepId);
    return categories.filter(c => stepCats.includes(c.category));
  };

  const handleUploadDocument = (category: string) => {
    setSelectedUploadCategory(category);
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
    setSelectedUploadCategory(undefined);
  };

  const handleUploadSuccess = async () => {
    // Refresh categories data after successful upload
    const categoriesRes = await KnowledgeBaseApi.getCategories();
    if (categoriesRes.success && categoriesRes.data) {
      setCategories(categoriesRes.data);
    }
  };

  const renderCompanyInfoStep = () => {
    // The four Company Profile categories also exist as KB document slots.
    // The textareas below save short summaries to the profile record; the
    // tiles further down let the user attach long-form documents (e.g. a
    // capability statement PDF) that the AI will retrieve from at draft time.
    const companyDocs = getCategoriesForStep('company');

    return (
    <>
      <TipBox>
        <TipIcon>
          <Info size={20} />
        </TipIcon>
        <TipContent>
          Start by entering your core company information. This data will be used to pre-fill common tender questions about your organization.
        </TipContent>
      </TipBox>

      <FormSection>
        <FormSectionHeader>
          <FormSectionIcon>
            <Building2 size={20} />
          </FormSectionIcon>
          <FormSectionTitle>Company Details</FormSectionTitle>
        </FormSectionHeader>

        <FormGrid>
          <FormGroup>
            <Label $hasError={!!validationErrors.company_name}>Company Name *</Label>
            <Input
              type="text"
              value={formData.company_name || ''}
              onChange={e => handleInputChange('company_name', e.target.value)}
              placeholder="Enter your company name"
              $hasError={!!validationErrors.company_name}
            />
            {validationErrors.company_name && (
              <FieldError>
                <AlertCircle size={14} />
                {validationErrors.company_name}
              </FieldError>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Trading Name</Label>
            <Input
              type="text"
              value={formData.trading_name || ''}
              onChange={e => handleInputChange('trading_name', e.target.value)}
              placeholder="Trading name (if different)"
            />
          </FormGroup>

          <FormGroup>
            <Label>Primary Industry</Label>
            <Select
              value={formData.primary_industry || ''}
              onChange={e => handleInputChange('primary_industry', e.target.value)}
            >
              <option value="">Select industry</option>
              {INDUSTRY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Number of Employees</Label>
            <Select
              value={formData.employee_count || ''}
              onChange={e => handleInputChange('employee_count', e.target.value)}
            >
              <option value="">Select range</option>
              {EMPLOYEE_COUNT_OPTIONS.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Year Established</Label>
            <Input
              type="number"
              value={formData.year_established || ''}
              onChange={e => handleInputChange('year_established', parseInt(e.target.value) || undefined)}
              placeholder="e.g., 2010"
              min={1800}
              max={new Date().getFullYear()}
            />
          </FormGroup>

          <FormGroup>
            <Label>Headquarters Country</Label>
            <Input
              type="text"
              value={formData.headquarters_country || ''}
              onChange={e => handleInputChange('headquarters_country', e.target.value)}
              placeholder="e.g., United Kingdom"
            />
          </FormGroup>

          <FormGroup>
            <Label>Website</Label>
            <Input
              type="url"
              value={formData.website || ''}
              onChange={e => handleInputChange('website', e.target.value)}
              placeholder="https://www.yourcompany.com"
            />
          </FormGroup>

          <FormGroup fullWidth>
            <p
              style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textMuted,
                margin: 0,
                lineHeight: theme.typography.lineHeight.relaxed,
              }}
            >
              Looking for the company description and mission statement? Those have moved to your{' '}
              <a
                href="/profile"
                style={{
                  color: theme.colors.primary[600],
                  textDecoration: 'underline',
                  fontWeight: theme.typography.fontWeight.medium,
                }}
                onClick={e => {
                  e.preventDefault();
                  navigate('/profile');
                }}
              >
                profile page
              </a>
              . The wizard now focuses on the core identity fields and document uploads the AI uses
              when drafting tender responses.
            </p>
          </FormGroup>
        </FormGrid>
      </FormSection>

      {companyDocs.length > 0 && (
        <FormSection>
          <FormSectionHeader>
            <FormSectionIcon>
              <Upload size={20} />
            </FormSectionIcon>
            <FormSectionTitle>Company Documents</FormSectionTitle>
          </FormSectionHeader>
          <p
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textMuted,
              marginBottom: theme.spacing[4],
              lineHeight: theme.typography.lineHeight.relaxed,
            }}
          >
            The fields above save short summaries to your profile. Upload longer documents
            here (capability statements, full company history, org charts) to power
            AI-generated tender responses.
          </p>
          <CategoryList>
            {companyDocs.map(cat => (
              <CategoryItem
                key={cat.category}
                hasDocument={cat.document_count > 0}
                isRecommended={cat.recommended_minimum > 0}
                isCritical={!!cat.critical && cat.document_count === 0}
              >
                <CategoryDetails>
                  <CategoryName>
                    {cat.display_name}
                    {cat.document_count === 0 && cat.critical ? (
                      <CriticalBadge>Critical</CriticalBadge>
                    ) : cat.recommended_minimum > 0 && cat.document_count === 0 ? (
                      <RecommendedBadge>Recommended</RecommendedBadge>
                    ) : null}
                  </CategoryName>
                  <CategoryDesc>{cat.description}</CategoryDesc>
                </CategoryDetails>
                <CategoryActions>
                  {cat.document_count > 0 && (
                    <UploadCountBadge>
                      <CheckCircle2 size={12} />
                      {cat.document_count} uploaded
                    </UploadCountBadge>
                  )}
                  <UploadButton
                    onClick={() => handleUploadDocument(cat.category)}
                  >
                    <Upload size={16} />
                    {cat.document_count > 0 ? 'Add more' : 'Upload'}
                  </UploadButton>
                </CategoryActions>
              </CategoryItem>
            ))}
          </CategoryList>
        </FormSection>
      )}
    </>
    );
  };

  const renderPerformanceStep = () => {
    // Performance step also surfaces upload tiles for two KB document slots
    // the wizard previously skipped: methodology (a critical category) and
    // capability_statement. The text fields below describe performance at
    // profile-level; the tiles let the user attach long-form evidence.
    const performanceDocs = getCategoriesForStep('performance');

    return (
    <>
      <TipBox>
        <TipIcon>
          <Info size={20} />
        </TipIcon>
        <TipContent>
          Add performance metrics, capabilities, and capacity information. This data helps generate evidence-based responses with quantifiable outcomes.
        </TipContent>
      </TipBox>

      {/* Performance Track Record */}
      <FormSection>
        <FormSectionHeader>
          <FormSectionIcon>
            <Briefcase size={20} />
          </FormSectionIcon>
          <FormSectionTitle>Performance Track Record</FormSectionTitle>
        </FormSectionHeader>

        <FormGrid>
          <FormGroup fullWidth>
            <Label>Performance Summary</Label>
            <TextArea
              value={formData.performance_summary || ''}
              onChange={e => handleInputChange('performance_summary', e.target.value)}
              placeholder="Summarize your track record, KPIs, and success rates..."
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <Label>On-Time Delivery Rate (%)</Label>
            <Input
              type="number"
              value={formData.on_time_delivery_rate ? formData.on_time_delivery_rate * 100 : ''}
              onChange={e => handleInputChange('on_time_delivery_rate', e.target.value ? parseFloat(e.target.value) / 100 : undefined)}
              placeholder="e.g., 98"
              min={0}
              max={100}
              step={0.1}
            />
          </FormGroup>

          <FormGroup>
            <Label>Budget Adherence Rate (%)</Label>
            <Input
              type="number"
              value={formData.budget_adherence_rate ? formData.budget_adherence_rate * 100 : ''}
              onChange={e => handleInputChange('budget_adherence_rate', e.target.value ? parseFloat(e.target.value) / 100 : undefined)}
              placeholder="e.g., 99.5"
              min={0}
              max={100}
              step={0.1}
            />
          </FormGroup>

          <FormGroup>
            <Label>Client Satisfaction Score (0-5)</Label>
            <Input
              type="number"
              value={formData.client_satisfaction_score || ''}
              onChange={e => handleInputChange('client_satisfaction_score', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="e.g., 4.8"
              min={0}
              max={5}
              step={0.1}
            />
          </FormGroup>
        </FormGrid>
      </FormSection>

      {/* Capabilities & Services */}
      <FormSection>
        <FormSectionHeader>
          <FormSectionIcon>
            <Award size={20} />
          </FormSectionIcon>
          <FormSectionTitle>Capabilities & Services</FormSectionTitle>
        </FormSectionHeader>

        <FormGrid>
          <FormGroup fullWidth>
            <Label>Capabilities Overview</Label>
            <TextArea
              value={formData.capabilities_overview || ''}
              onChange={e => handleInputChange('capabilities_overview', e.target.value)}
              placeholder="Describe your core capabilities and service offerings..."
              rows={3}
            />
          </FormGroup>
        </FormGrid>
      </FormSection>

      {/* Risk & Innovation */}
      <FormSection>
        <FormSectionHeader>
          <FormSectionIcon>
            <Shield size={20} />
          </FormSectionIcon>
          <FormSectionTitle>Risk Management & Innovation</FormSectionTitle>
        </FormSectionHeader>

        <FormGrid>
          <FormGroup fullWidth>
            <Label>Risk Management Approach</Label>
            <TextArea
              value={formData.risk_management_approach || ''}
              onChange={e => handleInputChange('risk_management_approach', e.target.value)}
              placeholder="Describe your approach to identifying and mitigating risks..."
              rows={3}
            />
          </FormGroup>

          <FormGroup fullWidth>
            <Label>Innovation Approach</Label>
            <TextArea
              value={formData.innovation_approach || ''}
              onChange={e => handleInputChange('innovation_approach', e.target.value)}
              placeholder="Describe your approach to innovation and continuous improvement..."
              rows={3}
            />
          </FormGroup>
        </FormGrid>
      </FormSection>

      {/* Social Value & Capacity */}
      <FormSection>
        <FormSectionHeader>
          <FormSectionIcon>
            <Users size={20} />
          </FormSectionIcon>
          <FormSectionTitle>Social Value & Delivery Capacity</FormSectionTitle>
        </FormSectionHeader>

        <FormGrid>
          <FormGroup fullWidth>
            <Label>Current Delivery Capacity</Label>
            <TextArea
              value={formData.current_capacity || ''}
              onChange={e => handleInputChange('current_capacity', e.target.value)}
              placeholder="Describe your current capacity and resource availability..."
              rows={2}
            />
          </FormGroup>

          <FormGroup fullWidth>
            <Label>Scalability Description</Label>
            <TextArea
              value={formData.scalability_description || ''}
              onChange={e => handleInputChange('scalability_description', e.target.value)}
              placeholder="Explain how your organization can scale to meet demand..."
              rows={2}
            />
          </FormGroup>
        </FormGrid>
      </FormSection>

      {performanceDocs.length > 0 && (
        <FormSection>
          <FormSectionHeader>
            <FormSectionIcon>
              <Upload size={20} />
            </FormSectionIcon>
            <FormSectionTitle>Capability Documents</FormSectionTitle>
          </FormSectionHeader>
          <p
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textMuted,
              marginBottom: theme.spacing[4],
              lineHeight: theme.typography.lineHeight.relaxed,
            }}
          >
            Upload long-form evidence for the AI to retrieve when drafting tender answers about
            how you deliver. Methodology is a critical category — without it, drafts can't ground
            "how will you deliver" questions in your actual approach.
          </p>
          <CategoryList>
            {performanceDocs.map(cat => (
              <CategoryItem
                key={cat.category}
                hasDocument={cat.document_count > 0}
                isRecommended={cat.recommended_minimum > 0}
                isCritical={!!cat.critical && cat.document_count === 0}
              >
                <CategoryDetails>
                  <CategoryName>
                    {cat.display_name}
                    {cat.document_count === 0 && cat.critical ? (
                      <CriticalBadge>Critical</CriticalBadge>
                    ) : cat.recommended_minimum > 0 && cat.document_count === 0 ? (
                      <RecommendedBadge>Recommended</RecommendedBadge>
                    ) : null}
                  </CategoryName>
                  <CategoryDesc>{cat.description}</CategoryDesc>
                </CategoryDetails>
                <CategoryActions>
                  {cat.document_count > 0 && (
                    <UploadCountBadge>
                      <CheckCircle2 size={12} />
                      {cat.document_count} uploaded
                    </UploadCountBadge>
                  )}
                  <UploadButton
                    onClick={() => handleUploadDocument(cat.category)}
                  >
                    <Upload size={16} />
                    {cat.document_count > 0 ? 'Add more' : 'Upload'}
                  </UploadButton>
                </CategoryActions>
              </CategoryItem>
            ))}
          </CategoryList>
        </FormSection>
      )}
    </>
    );
  };

  const renderDocumentUploadStep = (stepId: string) => {
    const stepCategories = getCategoriesForStep(stepId);

    // Group by CATEGORY_GROUPS
    const groupedCategories: Record<string, CategoryInfo[]> = {};
    Object.entries(CATEGORY_GROUPS).forEach(([groupName, groupCats]) => {
      const matching = stepCategories.filter(c => groupCats.includes(c.category));
      if (matching.length > 0) {
        groupedCategories[groupName] = matching;
      }
    });

    const stepTips: Record<string, string> = {
      team: 'Upload CVs of key team members and case studies to demonstrate your experience. These documents will be used to answer team capability and experience questions.',
      certifications: 'Upload your certifications and accreditations. Many tenders require specific ISO certifications and financial stability evidence.',
      policies: 'Upload your policy documents. Most public sector tenders require evidence of Health & Safety, Environmental, and Equality policies.',
    };

    return (
      <>
        <TipBox>
          <TipIcon>
            <Info size={20} />
          </TipIcon>
          <TipContent>{stepTips[stepId] || 'Upload relevant documents for this category.'}</TipContent>
        </TipBox>

        {Object.entries(groupedCategories).map(([groupName, groupCats]) => {
          const docsInGroup = groupCats.reduce((sum, c) => sum + c.document_count, 0);
          const isExpanded = expandedGroups[groupName] !== false;

          return (
            <CategoryGroupContainer key={groupName}>
              <CategoryGroupHeader
                isExpanded={isExpanded}
                onClick={() => toggleGroup(groupName)}
              >
                <CategoryGroupTitle>
                  <GroupEmoji>{CATEGORY_ICONS[groupName] || '📁'}</GroupEmoji>
                  <GroupName>{groupName}</GroupName>
                  <GroupBadge hasDocuments={docsInGroup > 0}>
                    {docsInGroup} document{docsInGroup !== 1 ? 's' : ''}
                  </GroupBadge>
                </CategoryGroupTitle>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </CategoryGroupHeader>

              {isExpanded && (
                <CategoryList>
                  {groupCats.map(cat => (
                    <CategoryItem
                      key={cat.category}
                      hasDocument={cat.document_count > 0}
                      isRecommended={cat.recommended_minimum > 0}
                      isCritical={!!cat.critical && cat.document_count === 0}
                    >
                      <CategoryDetails>
                        <CategoryName>
                          {cat.display_name}
                          {cat.document_count === 0 && cat.critical ? (
                            <CriticalBadge>Critical</CriticalBadge>
                          ) : cat.recommended_minimum > 0 && cat.document_count === 0 ? (
                            <RecommendedBadge>Recommended</RecommendedBadge>
                          ) : null}
                        </CategoryName>
                        <CategoryDesc>{cat.description}</CategoryDesc>
                      </CategoryDetails>
                      <CategoryActions>
                        {cat.document_count > 0 && (
                          <UploadCountBadge>
                            <CheckCircle2 size={12} />
                            {cat.document_count} uploaded
                          </UploadCountBadge>
                        )}
                        <UploadButton
                          onClick={() => handleUploadDocument(cat.category)}
                        >
                          <Upload size={16} />
                          {cat.document_count > 0 ? 'Add more' : 'Upload'}
                        </UploadButton>
                      </CategoryActions>
                    </CategoryItem>
                  ))}
                </CategoryList>
              )}
            </CategoryGroupContainer>
          );
        })}
      </>
    );
  };

  const renderReviewStep = () => {
    const totalDocs = categories.reduce((sum, c) => sum + c.document_count, 0);
    const recommendedCats = categories.filter(c => c.recommended_minimum > 0);
    const completedRecommended = recommendedCats.filter(c => c.document_count >= c.recommended_minimum);

    return (
      <>
        <FormSection>
          <FormSectionHeader>
            <FormSectionIcon>
              <Check size={20} />
            </FormSectionIcon>
            <FormSectionTitle>Setup Summary</FormSectionTitle>
          </FormSectionHeader>

          <div style={{ display: 'grid', gap: theme.spacing[4] }}>
            <div style={{
              padding: theme.spacing[4],
              background: theme.colors.backgroundTertiary,
              borderRadius: theme.borderRadius.lg,
            }}>
              <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing[2] }}>
                Company Profile
              </div>
              <div style={{ color: theme.colors.textSecondary }}>
                {formData.company_name || 'Not set'} | {formData.primary_industry || 'No industry'} | {formData.employee_count || 'No size'} employees
              </div>
            </div>

            <div style={{
              padding: theme.spacing[4],
              background: theme.colors.backgroundTertiary,
              borderRadius: theme.borderRadius.lg,
            }}>
              <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing[2] }}>
                Documents Uploaded
              </div>
              <div style={{ color: theme.colors.textSecondary }}>
                {totalDocs} documents across {categories.filter(c => c.document_count > 0).length} categories
              </div>
            </div>

            <div style={{
              padding: theme.spacing[4],
              background: completedRecommended.length === recommendedCats.length
                ? theme.colors.success[100]
                : theme.colors.warning[100],
              border: `1px solid ${completedRecommended.length === recommendedCats.length
                ? theme.colors.success[500]
                : theme.colors.warning[500]}`,
              borderRadius: theme.borderRadius.lg,
            }}>
              <div style={{
                fontWeight: theme.typography.fontWeight.semibold,
                marginBottom: theme.spacing[2],
                color: completedRecommended.length === recommendedCats.length
                  ? theme.colors.success[700]
                  : theme.colors.warning[700],
              }}>
                Recommended Categories
              </div>
              <div style={{
                color: completedRecommended.length === recommendedCats.length
                  ? theme.colors.success[600]
                  : theme.colors.warning[600],
              }}>
                {completedRecommended.length} of {recommendedCats.length} recommended categories completed
              </div>
            </div>
          </div>
        </FormSection>

        <TipBox>
          <TipIcon>
            <Info size={20} />
          </TipIcon>
          <TipContent>
            You can always add more documents later from the Knowledge Base page. The more documents you upload, the better the AI can generate contextually relevant tender responses.
          </TipContent>
        </TipBox>
      </>
    );
  };

  const renderStepContent = () => {
    const step = WIZARD_STEPS[currentStep];
    switch (step.id) {
      case 'company':
        return renderCompanyInfoStep();
      case 'performance':
        return renderPerformanceStep();
      case 'team':
      case 'certifications':
      case 'policies':
        return renderDocumentUploadStep(step.id);
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <WizardContainer>
            <div style={{ textAlign: 'center', padding: theme.spacing[16] }}>
              Loading setup wizard...
            </div>
          </WizardContainer>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <WizardContainer>
          <WizardHeader>
            <WizardTitle>Knowledge Base Setup</WizardTitle>
            <WizardDescription>
              Set up your company knowledge base to enable intelligent tender response generation
            </WizardDescription>
          </WizardHeader>

          {/* Progress Stepper */}
          <StepperContainer>
            <StepperLine>
              {WIZARD_STEPS.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <Step active={idx === currentStep} completed={idx < currentStep}>
                    <StepCircle active={idx === currentStep} completed={idx < currentStep}>
                      {idx < currentStep ? <Check size={20} /> : step.icon}
                    </StepCircle>
                    <StepLabel active={idx === currentStep}>{step.label}</StepLabel>
                  </Step>
                  {idx < WIZARD_STEPS.length - 1 && (
                    <StepConnector completed={idx < currentStep} />
                  )}
                </React.Fragment>
              ))}
            </StepperLine>
          </StepperContainer>

          {/* Step Content */}
          {renderStepContent()}

          {/* Navigation */}
          <NavigationButtons>
            <NavButton
              variant="secondary"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ArrowLeft size={18} />
              Previous
            </NavButton>

            {currentStep === WIZARD_STEPS.length - 1 ? (
              <NavButton variant="primary" onClick={handleComplete} disabled={saving}>
                Complete Setup
                <Check size={18} />
              </NavButton>
            ) : (
              <NavButton variant="primary" onClick={handleNext} disabled={saving}>
                {saving ? 'Saving...' : 'Next'}
                <ArrowRight size={18} />
              </NavButton>
            )}
          </NavigationButtons>
        </WizardContainer>
      </Container>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        initialCategory={selectedUploadCategory as any}
        onSuccess={handleUploadSuccess}
      />
    </PageContainer>
  );
};

export default KnowledgeBaseSetupPage;
